import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Card, ColorPicker, Image, List, Modal, Radio, Select, Slider, Space, Spin, Tag, Upload } from 'antd';
import { CameraOutlined, CheckCircleOutlined, ClearOutlined, DeleteOutlined, DownloadOutlined, HistoryOutlined, InboxOutlined, UploadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { preload, removeBackground } from '@imgly/background-removal';
import ProtectedToolWrapper from '../components/ProtectedToolWrapper';
import StandardButton from '../components/StandardButton';

const { Dragger } = Upload;
const { Option } = Select;
const BG_REMOVAL_TIMEOUT_MS = 120000;
const bgEngineConfig = {
  debug: true,
  device: 'cpu',
  model: 'isnet_fp16',
  progress: () => {
  }
};

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function dataUrlToBlob(dataUrl) {
  const [prefix, content] = dataUrl.split(',');
  const mime = prefix.match(/:(.*?);/)?.[1] || 'image/png';
  const binary = atob(content);
  const len = binary.length;
  const arr = new Uint8Array(len);
  for (let i = 0; i < len; i += 1) arr[i] = binary.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

const BackgroundRemover = () => {
  const { t } = useTranslation();
  const [originalImage, setOriginalImage] = useState(null);
  const [originalFile, setOriginalFile] = useState(null);
  const [foregroundUrl, setForegroundUrl] = useState('');
  const [resultImage, setResultImage] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [format, setFormat] = useState('png');
  const [bgType, setBgType] = useState('transparent');
  const [bgColor, setBgColor] = useState('#FFFFFF');
  const [blurAmount, setBlurAmount] = useState(5);
  const [customBgImage, setCustomBgImage] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [processingFileName, setProcessingFileName] = useState('');
  const [engineError, setEngineError] = useState('');

  const foregroundObjectUrlRef = useRef(null);
  const downloadObjectUrlRef = useRef(null);

  const outputMime = useMemo(() => {
    if (format === 'jpg' || format === 'jpeg') return 'image/jpeg';
    if (format === 'webp') return 'image/webp';
    return 'image/png';
  }, [format]);

  const composeResult = async (inputOriginalImage, inputForegroundUrl) => {
    if (!inputOriginalImage || !inputForegroundUrl) return '';

    const foreground = await loadImage(inputForegroundUrl);
    const original = await loadImage(inputOriginalImage);

    const canvas = document.createElement('canvas');
    canvas.width = foreground.width;
    canvas.height = foreground.height;
    const ctx = canvas.getContext('2d');

    if (bgType === 'transparent') {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    } else if (bgType === 'color') {
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    } else if (bgType === 'blur') {
      ctx.filter = `blur(${blurAmount}px)`;
      ctx.drawImage(original, 0, 0, canvas.width, canvas.height);
      ctx.filter = 'none';
    } else if (bgType === 'custom' && customBgImage) {
      const custom = await loadImage(customBgImage);
      ctx.drawImage(custom, 0, 0, canvas.width, canvas.height);
    }

    ctx.drawImage(foreground, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL(outputMime, outputMime === 'image/png' ? undefined : 0.95);
  };

  const saveToHistory = (inputOriginalImage, resultUrl) => {
    const historyItem = {
      id: Date.now(),
      originalImage: inputOriginalImage,
      resultUrl,
      timestamp: new Date().toISOString(),
      settings: { bgType, bgColor, blurAmount, format }
    };
    const existing = JSON.parse(localStorage.getItem('background_remover_history') || '[]');
    const next = [historyItem, ...existing].slice(0, 50);
    localStorage.setItem('background_remover_history', JSON.stringify(next));
    setHistory(next);
  };

  const processImage = async (file) => {
    setLoading(true);
    setEngineError('');
    setProcessingStatus(t('bg_status_processing') || 'Removing background...');
    setProcessingFileName(file?.name || '');

    try {
      const originalDataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target?.result || '');
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      setOriginalFile(file);
      setOriginalImage(originalDataUrl);

      const foregroundBlob = await Promise.race([
        removeBackground(file, bgEngineConfig),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('BG_REMOVAL_TIMEOUT')), BG_REMOVAL_TIMEOUT_MS)
        )
      ]);
      const fgUrl = URL.createObjectURL(foregroundBlob);
      if (foregroundObjectUrlRef.current) URL.revokeObjectURL(foregroundObjectUrlRef.current);
      foregroundObjectUrlRef.current = fgUrl;
      setForegroundUrl(fgUrl);

      const composed = await composeResult(originalDataUrl, fgUrl);
      setResultImage(composed);
      setProcessingStatus(t('bg_status_done') || 'Background removed successfully');
      saveToHistory(originalDataUrl, composed);
    } catch (error) {
      const isTimeout = String(error?.message || '').includes('BG_REMOVAL_TIMEOUT');
      setEngineError(
        isTimeout
          ? (t('bg_process_timeout') || 'Background removal timed out. Please try a smaller image or retry.')
          : (t('bg_process_failed') || 'Failed to process image. Please try again.')
      );
      setProcessingStatus(t('bg_status_failed') || 'Background removal failed');
      toast.error(
        isTimeout
          ? (t('bg_process_timeout') || 'Background removal timed out. Please try a smaller image or retry.')
          : (t('bg_process_failed') || 'Failed to process image. Please try again.')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return Upload.LIST_IGNORE;
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('bg_file_too_large') || 'File size must be less than 10MB');
      return Upload.LIST_IGNORE;
    }
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/bmp'];
    if (!validTypes.includes(file.type)) {
      toast.error(t('bg_invalid_file_type') || 'Please upload a JPEG, PNG, WEBP, or BMP image');
      return Upload.LIST_IGNORE;
    }

    await processImage(file);
    return Upload.LIST_IGNORE;
  };

  const handleDownload = () => {
    if (!resultImage) {
      toast.error(t('nothing_to_download') || 'Nothing to download');
      return;
    }
    const link = document.createElement('a');
    link.download = `background_removed_${Date.now()}.${format}`;
    link.href = downloadUrl || resultImage;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(t('download_started') || 'Download started');
  };

  const handleClear = () => {
    setOriginalImage(null);
    setOriginalFile(null);
    setForegroundUrl('');
    setResultImage(null);
    setCustomBgImage(null);
    setProcessingFileName('');
    setProcessingStatus('');
    setEngineError('');
    if (foregroundObjectUrlRef.current) {
      URL.revokeObjectURL(foregroundObjectUrlRef.current);
      foregroundObjectUrlRef.current = null;
    }
    if (downloadObjectUrlRef.current) {
      URL.revokeObjectURL(downloadObjectUrlRef.current);
      downloadObjectUrlRef.current = null;
      setDownloadUrl('');
    }
  };

  const handleCustomBgUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => setCustomBgImage(e.target?.result || null);
    reader.readAsDataURL(file);
    return false;
  };

  const handleHistoryItemClick = (item) => {
    setOriginalImage(item.originalImage);
    setResultImage(item.resultUrl);
    setShowHistory(false);
    setProcessingStatus(t('bg_status_done') || 'Background removed successfully');
    toast.success(t('history_item_loaded') || 'Image loaded from history');
  };

  const handleDeleteHistory = (id) => {
    const next = history.filter((item) => item.id !== id);
    localStorage.setItem('background_remover_history', JSON.stringify(next));
    setHistory(next);
    toast.success(t('deleted_successfully') || 'Deleted successfully');
  };

  const handleClearHistory = () => {
    Modal.confirm({
      title: t('clear_history_confirm') || 'Clear all history?',
      content: t('clear_history_confirm_message') || 'This action cannot be undone.',
      onOk: () => {
        localStorage.removeItem('background_remover_history');
        setHistory([]);
        toast.success(t('history_cleared') || 'History cleared');
      }
    });
  };

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem('background_remover_history') || '[]');
    setHistory(saved);
  }, []);

  useEffect(() => {
    preload(bgEngineConfig)
      .catch(() => {
      });
  }, []);

  useEffect(() => {
    if (!originalImage || !foregroundUrl) return;
    if (loading) return;

    let active = true;
    (async () => {
      try {
        setProcessingStatus(t('bg_status_processing') || 'Removing background...');
        const composed = await composeResult(originalImage, foregroundUrl);
        if (!active) return;
        setResultImage(composed);
        setProcessingStatus(t('bg_status_done') || 'Background removed successfully');
      } catch (error) {
        if (!active) return;
        setProcessingStatus(t('bg_status_failed') || 'Background removal failed');
      }
    })();

    return () => {
      active = false;
    };
  }, [bgType, bgColor, blurAmount, customBgImage, format]);

  useEffect(() => {
    if (!resultImage) {
      if (downloadObjectUrlRef.current) {
        URL.revokeObjectURL(downloadObjectUrlRef.current);
        downloadObjectUrlRef.current = null;
      }
      setDownloadUrl('');
      return;
    }
    const blob = dataUrlToBlob(resultImage);
    if (downloadObjectUrlRef.current) URL.revokeObjectURL(downloadObjectUrlRef.current);
    const next = URL.createObjectURL(blob);
    downloadObjectUrlRef.current = next;
    setDownloadUrl(next);
  }, [resultImage]);

  useEffect(() => () => {
    if (foregroundObjectUrlRef.current) URL.revokeObjectURL(foregroundObjectUrlRef.current);
    if (downloadObjectUrlRef.current) URL.revokeObjectURL(downloadObjectUrlRef.current);
  }, []);

  return (
    <ProtectedToolWrapper>
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Card className="rounded-xl shadow-lg">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold">{t('background_remover_title') || 'Background Remover'}</h1>
              <p className="text-gray-500 mt-1">{t('background_remover_subtitle') || 'Remove image backgrounds instantly using AI - runs locally in your browser'}</p>
            </div>
            <Space>
              <StandardButton icon={<HistoryOutlined />} onClick={() => setShowHistory(true)} type="secondary">
                {t('history') || 'History'} ({history.length})
              </StandardButton>
              <StandardButton icon={<ClearOutlined />} onClick={handleClear} type="secondary" disabled={!originalImage}>
                {t('clear') || 'Clear'}
              </StandardButton>
            </Space>
          </div>

          {engineError ? (
            <Alert message={t('bg_model_loading_error_title') || 'Model Loading Error'} description={engineError} type="error" showIcon className="mb-4" />
          ) : null}

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold mb-3">{t('original_image') || 'Original Image'}</h3>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 min-h-[400px] flex items-center justify-center bg-gray-50">
                {originalImage ? (
                  <img src={originalImage} alt="Original" className="max-w-full max-h-[400px] object-contain" />
                ) : (
                  <div className="text-center">
                    <InboxOutlined className="text-4xl text-gray-400 mb-2" />
                    <p className="text-gray-500">{t('drop_image_here') || 'Drop your image here or click to upload'}</p>
                    <p className="text-gray-400 text-sm mt-2">{t('supported_formats') || 'Supports JPG, PNG, WEBP, BMP (max 10MB)'}</p>
                    <p className="text-green-600 text-sm mt-2"><CheckCircleOutlined /> {t('works_offline') || 'Works completely offline in your browser'}</p>
                  </div>
                )}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3">{t('result_image') || 'Result'}</h3>
              <div className="border-2 border-gray-300 rounded-lg p-4 min-h-[400px] flex items-center justify-center bg-gray-50">
                {loading ? (
                  <div className="text-center">
                    <Spin size="large" />
                    <p className="mt-2 text-gray-500">{processingStatus || (t('bg_status_processing') || 'Removing background...')}</p>
                  </div>
                ) : resultImage ? (
                  <div className="text-center w-full">
                    <img src={resultImage} alt="Result" className="max-w-full max-h-[360px] object-contain mx-auto" />
                    <p className="mt-3 text-green-600 text-sm">{processingStatus || (t('bg_status_done') || 'Background removed successfully')}</p>
                  </div>
                ) : (
                  <div className="text-center text-gray-500">
                    <CameraOutlined className="text-4xl text-gray-400 mb-2" />
                    <p>{t('result_will_appear_here') || 'Result will appear here'}</p>
                    {processingStatus ? <p className="mt-2 text-sm">{processingStatus}</p> : null}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mt-6">
            <Dragger
              name="image"
              multiple={false}
              beforeUpload={handleUpload}
              showUploadList={false}
              accept="image/jpeg,image/jpg,image/png,image/webp,image/bmp"
              disabled={loading}
            >
              <p className="ant-upload-drag-icon"><UploadOutlined /></p>
              <p className="ant-upload-text">{t('click_or_drag_to_upload') || 'Click or drag image to upload'}</p>
              <p className="ant-upload-hint">
                {processingFileName
                  ? `${t('selected_file') || 'Selected file'}: ${processingFileName}`
                  : (t('upload_hint') || 'Upload an image to remove its background. All processing happens locally in your browser - no servers involved! Your images stay private.')}
              </p>
            </Dragger>
          </div>

          <div className="mt-6">
            <h3 className="text-lg font-semibold mb-3">{t('background_settings') || 'Background Settings'}</h3>
            <div className="space-y-4">
              <Radio.Group value={bgType} onChange={(e) => setBgType(e.target.value)} buttonStyle="solid">
                <Radio.Button value="transparent">{t('transparent') || 'Transparent'}</Radio.Button>
                <Radio.Button value="color">{t('solid_color') || 'Solid Color'}</Radio.Button>
                <Radio.Button value="blur">{t('blur_background') || 'Blur Background'}</Radio.Button>
                <Radio.Button value="custom">{t('custom_image') || 'Custom Image'}</Radio.Button>
              </Radio.Group>

              {bgType === 'color' ? (
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <span>{t('choose_color') || 'Choose color:'}</span>
                  <ColorPicker value={bgColor} onChange={(color) => setBgColor(color.toHexString())} />
                  <div className="w-10 h-10 rounded border border-gray-300 shadow-sm" style={{ backgroundColor: bgColor }} />
                  <span className="text-sm text-gray-500">{bgColor}</span>
                </div>
              ) : null}

              {bgType === 'blur' ? (
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <span>{t('blur_amount') || 'Blur amount:'}</span>
                  <Slider min={0} max={20} value={blurAmount} onChange={setBlurAmount} style={{ width: 220 }} />
                  <span className="font-medium">{blurAmount}px</span>
                </div>
              ) : null}

              {bgType === 'custom' ? (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <Upload beforeUpload={handleCustomBgUpload} showUploadList={false} accept="image/*">
                    <StandardButton icon={<UploadOutlined />}>
                      {t('upload_background_image') || 'Upload Background Image'}
                    </StandardButton>
                  </Upload>
                  {customBgImage ? (
                    <div className="mt-3">
                      <p className="text-sm text-gray-500 mb-2">Background preview:</p>
                      <img src={customBgImage} alt="Background" className="w-32 h-32 object-cover rounded-lg border" />
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium mb-2">{t('output_format') || 'Output Format'}</label>
            <Select value={format} onChange={setFormat} style={{ width: 240 }}>
              <Option value="png">PNG (Transparent, Best Quality)</Option>
              <Option value="webp">WEBP (Good Quality, Small Size)</Option>
              <Option value="jpg">JPG (No Transparency, Smallest)</Option>
            </Select>
          </div>

          <div className="mt-6 flex gap-3">
            <StandardButton type="primary" onClick={handleDownload} disabled={!resultImage || loading} icon={<DownloadOutlined />} size="large">
              {t('download_result') || 'Download Result'}
            </StandardButton>
          </div>
        </Card>

        <Modal
          title={<div className="flex items-center gap-2"><HistoryOutlined /><span>{t('history') || 'History'}</span><Tag color="blue">{history.length} {t('items') || 'items'}</Tag></div>}
          open={showHistory}
          onCancel={() => setShowHistory(false)}
          footer={[
            <StandardButton key="clear" type="secondary" danger onClick={handleClearHistory} disabled={history.length === 0} icon={<DeleteOutlined />}>
              {t('clear_all') || 'Clear All'}
            </StandardButton>,
            <StandardButton key="close" type="primary" onClick={() => setShowHistory(false)}>
              {t('close') || 'Close'}
            </StandardButton>
          ]}
          width={800}
        >
          {history.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <CameraOutlined className="text-5xl text-gray-300 mb-3" />
              <p>{t('no_history') || 'No history yet. Remove backgrounds and they will appear here.'}</p>
            </div>
          ) : (
            <List
              dataSource={history}
              renderItem={(item) => (
                <List.Item
                  actions={[
                    <StandardButton key="view" variant="ghost" size="small" onClick={() => handleHistoryItemClick(item)} icon={<CameraOutlined />}>
                      {t('load') || 'Load'}
                    </StandardButton>,
                    <StandardButton key="delete" variant="ghost" size="small" danger onClick={() => handleDeleteHistory(item.id)} icon={<DeleteOutlined />}>
                      {t('delete') || 'Delete'}
                    </StandardButton>
                  ]}
                >
                  <List.Item.Meta
                    avatar={<Image width={70} height={70} src={item.resultUrl} preview={false} style={{ objectFit: 'cover', borderRadius: 8 }} />}
                    title={<div className="flex items-center gap-2"><span className="font-medium">Processed Image</span><Tag color="green">{item.settings?.format?.toUpperCase() || 'PNG'}</Tag></div>}
                    description={<div className="text-xs text-gray-500">{new Date(item.timestamp).toLocaleString()}</div>}
                  />
                </List.Item>
              )}
            />
          )}
        </Modal>
      </div>
    </ProtectedToolWrapper>
  );
};

export default BackgroundRemover;
